import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LogOut, Inbox, FileText, MessageSquare, Circle, Trash2, Loader2, RefreshCw, Download, ExternalLink, FileBarChart2, Calculator, Swords, Leaf, ShieldCheck, Factory, Search, Mail, Send, TrendingUp, Users, X, Copy, Activity, Flame, Eye, Boxes, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { IMAGES } from "../lib/data";
import { COLLATERAL } from "../lib/collateral";
import OutreachCampaigns from "../components/admin/OutreachCampaigns";
import VisitorAnalytics from "../components/admin/VisitorAnalytics";
import ActivityFeed from "../components/admin/ActivityFeed";
import RangeAnalytics from "../components/admin/RangeAnalytics";
import PartnersAdmin from "../components/admin/PartnersAdmin";
import MatManager from "../components/admin/MatManager";

const MAT_SECTORS = ["Oil & Gas", "Pipeline", "Heavy / Civil Construction", "Utility / T&D", "Fire / Hazmat", "Industrial Facilities", "Municipal"];
const CHEM_DOC_TYPES = [
  { t: "Value Proposition Matrix", path: "value-matrix", pdf: "value-matrix", icon: FileText, roles: ["EHS / Environmental", "Superintendent", "Maintenance / Facilities", "Procurement", "Compliance"], desc: "Persona sell sheet: tailored pain / solution / outcome for 5 buyer roles." },
  { t: "ROI / Cost-Benefit Brief", path: "roi", pdf: "roi-brief", icon: FileBarChart2, roles: ["Procurement", "EHS / Environmental"], desc: "Cost-benefit one-pager built from the product's indexed savings metrics." },
  { t: "Technical Data Sheet", path: "datasheet", pdf: "data-sheet", icon: FileText, roles: ["Maintenance / Facilities", "Compliance", "Superintendent"], desc: "Spec sheet: physical, performance, application & environmental data." },
  { t: "Competitive Battlecard", path: "battlecard", pdf: "battlecard", icon: Swords, roles: ["Procurement", "Superintendent"], desc: "Head-to-head vs the conventional alternative across cost-driving specs." },
  { t: "Case Study / Project Profile", path: "case-study", pdf: "case-study", icon: FileText, roles: ["EHS / Environmental", "Superintendent", "Procurement"], desc: "Project profile with challenge, deployment & measured results." },
  { t: "Environmental & Compliance Brief", path: "compliance", pdf: "compliance-brief", icon: Leaf, roles: ["EHS / Environmental", "Compliance"], desc: "Environmental & compliance one-pager for bid & permit submittals." },
  { t: "Interactive ROI Calculator", path: "calculator", pdf: null, icon: Calculator, tool: true, roles: ["Procurement", "EHS / Environmental"], desc: "Live lead-gen tool: prospects model savings & email the analysis." },
];
const CHEM_DOCS = Object.values(COLLATERAL).flatMap((c) =>
  CHEM_DOC_TYPES.map((dt) => ({
    icon: dt.icon,
    title: `${c.name} — ${dt.t}`,
    desc: dt.desc,
    view: `/collateral/${c.slug}/${dt.path}`,
    pdf: dt.pdf ? `/${c.slug}-${dt.pdf}.pdf` : undefined,
    tool: dt.tool,
    product: c.name,
    sectors: c.sectors,
    roles: dt.roles,
  }))
);

const HOT_LEAD_THRESHOLD = 3;

// Fire & Hazmat PinkWater collateral — ADMIN ONLY (removed from public site per client request).
// PDFs are served from a protected backend endpoint (auth via ?token=). `file` = protected filename.
const FIRE_HAZMAT_DOCS = [
  { icon: Flame, title: "Pinkwater — Fire & Hazmat Marketing Flyer", desc: "Fire/hazmat sales flyer. ADMIN ONLY — not shown on the public site.", view: "/pinkwater-fire-marketing-flyer", file: "pinkwater-fire-marketing-flyer.pdf", product: "Pinkwater", sectors: ["Fire / Hazmat", "Municipal"], roles: ["EHS / Environmental", "Compliance", "Procurement"] },
  { icon: FileText, title: "Pinkwater — Fire & Hazmat Educational Flyer", desc: "What PinkWater is, how it works, and how it differs from AFFF. ADMIN ONLY.", view: "/pinkwater-educational-flyer", file: "pinkwater-educational-flyer.pdf", product: "Pinkwater", sectors: ["Fire / Hazmat", "Municipal"], roles: ["EHS / Environmental", "Compliance"] },
];

// Fire Service (Pinkwater complementary-to-AFFF) collateral — public-path PDFs, linked only in admin.
const FIRE_SERVICE_DOCS = [
  { icon: Flame, title: "Pinkwater — Fire Service Sales Flyer", desc: 'Complementary-to-AFFF hand-out: "AFFF helps extinguish the fire. Pinkwater helps finish the job." Pain points + benefits.', view: "/pinkwater-fire-sales-flyer.pdf", pdf: "/pinkwater-fire-sales-flyer.pdf", product: "Pinkwater", sectors: ["Fire / Hazmat", "Municipal"], roles: ["Fire Chief / Officer", "EHS / Environmental", "Procurement"] },
  { icon: MessageSquare, title: "Pinkwater — Fire Service Discovery & Talk Track", desc: "Internal sales enablement: consultative opening, pain-point questions, positioning language, and Fire Chief discovery questions.", view: "/pinkwater-fire-discovery-talk-track.pdf", pdf: "/pinkwater-fire-discovery-talk-track.pdf", product: "Pinkwater", sectors: ["Fire / Hazmat", "Municipal"], roles: ["Procurement", "EHS / Environmental"] },
  { icon: ShieldCheck, title: "Pinkwater — Live Field Demonstration Guide", desc: "Side-by-side demo protocol (water vs degreaser vs Pinkwater) with metrics, setup, safety, and a fillable scorecard.", view: "/pinkwater-fire-demo-guide.pdf", pdf: "/pinkwater-fire-demo-guide.pdf", product: "Pinkwater", sectors: ["Fire / Hazmat", "Municipal"], roles: ["Fire Chief / Officer", "EHS / Environmental"] },
  { icon: FileText, title: "Pinkwater — Fire Service Capabilities & Value Sheet", desc: "Applications-by-incident matrix, benefits, and environmental stewardship — complementary, not a foam replacement.", view: "/pinkwater-fire-capabilities-sheet.pdf", pdf: "/pinkwater-fire-capabilities-sheet.pdf", product: "Pinkwater", sectors: ["Fire / Hazmat", "Municipal"], roles: ["Fire Chief / Officer", "Compliance", "Procurement"] },
  { icon: FileText, title: "Pinkwater — Woodlands FD Tailored Proposal (Template)", desc: "Customizable department proposal: situation, applications, discovery inputs, and a demo next-step. Swap in real figures.", view: "/pinkwater-woodlands-fd-proposal.pdf", pdf: "/pinkwater-woodlands-fd-proposal.pdf", product: "Pinkwater", sectors: ["Fire / Hazmat", "Municipal"], roles: ["Fire Chief / Officer", "Procurement"] },
];

const STATUS_STYLE = {
  new: "bg-flame/15 text-flame border-flame/40",
  in_progress: "bg-blue-500/15 text-blue-600 border-blue-500/40",
  closed: "bg-emerald-500/15 text-emerald-600 border-emerald-500/40",
};

const SOURCE_STYLE = {
  Calculator: "text-flame border-flame/40",
  Download: "text-amber-500 border-amber-500/40",
  "Quote Form": "text-blue-400 border-blue-400/40",
  Contact: "text-white/60 border-white/20",
  Outreach: "text-emerald-400 border-emerald-400/40",
};

export function leadSource(s) {
  if ((s.data?.source || "") === "Outreach") return "Outreach";
  const ind = (s.data?.industry || "").toLowerCase();
  const msg = (s.data?.message || "").toLowerCase();
  if (ind.includes("outreach")) return "Outreach";
  if (ind.includes("roi calculator") || ind.includes("ground protection roi")) return "Calculator";
  if (ind.includes("download") || msg.includes("resource download")) return "Download";
  if (s.type === "contact") return "Contact";
  return "Quote Form";
}
export function leadProduct(s) {
  const t = ((s.data?.industry || "") + " " + (s.data?.message || "")).toLowerCase();
  if (t.includes("big bad") || t.includes("ground protection roi")) return "Big Bad Mats";
  if (t.includes("bonedry")) return "BoneDry";
  if (t.includes("pinkwater")) return "Pinkwater";
  return "—";
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [subs, setSubs] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [docProduct, setDocProduct] = useState("All Products");
  const [docSector, setDocSector] = useState("All Sectors");
  const [docRole, setDocRole] = useState("All Roles");
  const [docSearch, setDocSearch] = useState("");
  const [subSource, setSubSource] = useState("All Sources");
  const [subProduct, setSubProduct] = useState("All Products");
  const [sortBy, setSortBy] = useState("recent");
  const [tab, setTab] = useState("leads");
  const [collateralOpen, setCollateralOpen] = useState(false);
  const [newOnly, setNewOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { type: filter };
      const [s, list, ec] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/submissions", { params }),
        api.get("/admin/engagement-counts"),
      ]);
      setStats(s.data);
      setSubs(list.data);
      setCounts(ec.data || {});
    } catch (e) {
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (user === false) navigate("/login");
    if (user) load();
  }, [user, filter, load, navigate]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/submissions/${id}`, { status });
      setSubs((prev) => prev.map((s) => (s.id === id ? { ...s, status } : s)));
      if (selected?.id === id) setSelected({ ...selected, status });
      if (detail?.id === id) setDetail({ ...detail, status });
      toast.success("Status updated");
    } catch (e) { toast.error("Update failed"); }
  };

  const openSubmission = async (s) => {
    setSelected(s);
    setDetail(null);
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/submissions/${s.id}`);
      setDetail(data);
    } catch (e) { toast.error("Failed to load lead detail"); }
    finally { setDetailLoading(false); }
  };
  const closeModal = () => { setSelected(null); setDetail(null); };
  const copy = (text) => { navigator.clipboard?.writeText(text); toast.success("Copied to clipboard"); };

  const exportCsv = () => {
    const headers = ["Name", "Email", "Company", "Phone", "Source", "Product", "Type", "Status", "Industry", "Date"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = visibleSubs.map((s) => [
      s.data.name || "", s.data.email || "", s.data.company || "", s.data.phone || "",
      leadSource(s), leadProduct(s), s.type === "quote" ? "Quote Request" : "Contact Message",
      s.status, s.data.industry || "", s.created_at ? new Date(s.created_at).toLocaleString() : "",
    ]);
    const csv = [headers, ...rows].map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `oneharvest-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} lead${rows.length === 1 ? "" : "s"} to CSV`);
  };

  const openSubmissionById = useCallback(async (id) => {
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/submissions/${id}`);
      setSelected({ id: data.id, type: data.type, status: data.status, created_at: data.created_at, data: data.data });
      setDetail(data);
    } catch (e) { toast.error("Lead not found"); }
    finally { setDetailLoading(false); }
  }, []);

  // Handle Slack/email deep-links: /admin?lead=<id> and /admin?tab=<tab>
  useEffect(() => {
    if (!user) return;
    const t = searchParams.get("tab");
    const lead = searchParams.get("lead");
    if (!t && !lead) return;
    if (t) setTab(t);
    if (lead) { setTab("leads"); openSubmissionById(lead); }
    searchParams.delete("tab"); searchParams.delete("lead");
    setSearchParams(searchParams, { replace: true });
  }, [user, searchParams, setSearchParams, openSubmissionById]);

  const remove = async (id) => {
    try {
      await api.delete(`/admin/submissions/${id}`);
      setSubs((prev) => prev.filter((s) => s.id !== id));
      setSelected(null);
      setDetail(null);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  const [sendingDigest, setSendingDigest] = useState(false);
  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post("/admin/send-digest");
      toast.success(`Weekly digest emailed — ${data.leads} lead${data.leads === 1 ? "" : "s"}`);
    } catch (e) { toast.error("Failed to send digest"); }
    finally { setSendingDigest(false); }
  };

  const topEngaged = useMemo(() => {
    const byEmail = {};
    subs.forEach((s) => {
      const email = (s.data.email || "").toLowerCase();
      if (!email) return;
      if (!byEmail[email]) byEmail[email] = { email, total: counts[email]?.total || 0, sub: s };
      else if ((s.created_at || "") > (byEmail[email].sub.created_at || "")) byEmail[email].sub = s;
    });
    return Object.values(byEmail).filter((x) => x.total > 1).sort((a, b) => b.total - a.total).slice(0, 3);
  }, [subs, counts]);

  if (user === null) {
    return <div className="min-h-screen flex items-center justify-center bg-charcoal"><Loader2 className="h-6 w-6 animate-spin text-flame" /></div>;
  }

  const STAT_CARDS = stats ? [
    { label: "Total", value: stats.total, icon: Inbox },
    { label: "Quote Requests", value: stats.quotes, icon: FileText },
    { label: "Contact Messages", value: stats.contacts, icon: MessageSquare },
    { label: "New / Unread", value: stats.new, icon: Circle },
  ] : [];

  const BBM_RAW = [
    {
      icon: FileBarChart2,
      title: "Big Bad Mats — ROI Unit-Economics Brief",
      desc: "One-page procurement sell sheet: cost-of-ownership modeling, break-even & fleet savings.",
      view: "/big-bad-mats-roi",
      pdf: "/big-bad-mats-roi-brief.pdf",
    },
    {
      icon: FileBarChart2,
      title: "Big Bad Mats — TCO Framework (Procurement)",
      desc: "Business-case one-pager: portfolio optimization, install ROI & replacement avoidance.",
      view: "/big-bad-mats-tco",
      pdf: "/big-bad-mats-tco-framework.pdf",
    },
    {
      icon: FileText,
      title: "Big Bad Mats — Value Proposition Matrix",
      desc: "Persona sell sheet: tailored pain / solution / outcome for 5 buyer roles.",
      view: "/big-bad-mats-value-matrix",
      pdf: "/big-bad-mats-value-matrix.pdf",
    },
    {
      icon: FileBarChart2,
      title: "Big Bad Mats — Asset Utilization Brief",
      desc: "Equipment/Asset Manager one-pager: kill replacements, maximize utilization & turns.",
      view: "/big-bad-mats-asset-roi",
      pdf: "/big-bad-mats-asset-roi.pdf",
    },
    {
      icon: FileText,
      title: "Big Bad Mats — Site Safety / HSE Brief",
      desc: "HSE Director one-pager: cut ground-crew exposure hours, trip hazards & TRIR.",
      view: "/big-bad-mats-safety",
      pdf: "/big-bad-mats-safety-brief.pdf",
    },
    {
      icon: FileText,
      title: "Big Bad Mats — Schedule / Superintendent Brief",
      desc: "GC Superintendent one-pager: schedule compression, urban footprint & zero fines.",
      view: "/big-bad-mats-schedule",
      pdf: "/big-bad-mats-schedule-brief.pdf",
    },
    {
      icon: FileText,
      title: "Big Bad Mats — Engineering / Structural Brief",
      desc: "Civil Engineer one-pager: load distribution, subgrade protection & compliance.",
      view: "/big-bad-mats-engineering",
      pdf: "/big-bad-mats-engineering-brief.pdf",
    },
    {
      icon: FileBarChart2,
      title: "Big Bad Mats — Global Sourcing / TCO Brief",
      desc: "Procurement Director one-pager: financial triad, CapEx compression & lifecycle ROI.",
      view: "/big-bad-mats-sourcing",
      pdf: "/big-bad-mats-sourcing-brief.pdf",
    },
    {
      icon: FileText,
      title: "Big Bad Mats — Technical Data Sheet",
      desc: "Spec sheet: dimensions, load ratings, material, install & compliance data.",
      view: "/big-bad-mats-datasheet",
      pdf: "/big-bad-mats-technical-data-sheet.pdf",
    },
    {
      icon: Calculator,
      title: "Big Bad Mats — Interactive ROI Calculator",
      desc: "Live lead-gen tool: prospects model TCO & email themselves the analysis.",
      view: "/big-bad-mats-calculator",
      tool: true,
    },
    {
      icon: Swords,
      title: "Big Bad Mats — Competitive Battlecard",
      desc: "Head-to-head vs composite, timber & steel across TCO-driving specs.",
      view: "/big-bad-mats-battlecard",
      pdf: "/big-bad-mats-battlecard.pdf",
    },
    {
      icon: FileText,
      title: "Big Bad Mats — Case Study / Project Profile",
      desc: "60-day pipeline ROW: 5,000 mats, zero replacements, measured results.",
      view: "/big-bad-mats-case-study",
      pdf: "/big-bad-mats-case-study.pdf",
    },
    {
      icon: Leaf,
      title: "Big Bad Mats — Sustainability / ESG Brief",
      desc: "Recyclable, non-leach, lower lifecycle footprint — for bid & ESG submittals.",
      view: "/big-bad-mats-sustainability",
      pdf: "/big-bad-mats-sustainability.pdf",
    },
    {
      icon: ShieldCheck,
      title: "Big Bad Mats — Warranty & Certifications",
      desc: "Warranty statement, conformance & submittal package (CAD/FEA on request).",
      view: "/big-bad-mats-warranty",
      pdf: "/big-bad-mats-warranty.pdf",
    },
    {
      icon: Factory,
      title: "Big Bad Mats — Oil & Gas Brief",
      desc: "Vertical one-pager: remote access, containment & utilization.",
      view: "/big-bad-mats-industry/oil-gas",
      pdf: "/big-bad-mats-industry-oil-gas.pdf",
    },
    {
      icon: Factory,
      title: "Big Bad Mats — Pipeline ROW Brief",
      desc: "Vertical one-pager: subgrade protection, clean handback & reuse.",
      view: "/big-bad-mats-industry/pipeline-row",
      pdf: "/big-bad-mats-industry-pipeline-row.pdf",
    },
    {
      icon: Factory,
      title: "Big Bad Mats — Utility / T&D Brief",
      desc: "Vertical one-pager: rapid access, crew safety & versatility.",
      view: "/big-bad-mats-industry/utility-td",
      pdf: "/big-bad-mats-industry-utility-td.pdf",
    },
    {
      icon: Factory,
      title: "Big Bad Mats — Fire / Hazmat Brief",
      desc: "Vertical one-pager: decon-ready, rapid staging & zero contamination.",
      view: "/big-bad-mats-industry/fire-hazmat",
      pdf: "/big-bad-mats-industry-fire-hazmat.pdf",
    },
    {
      icon: FileText,
      title: "Big Bad Mats — Executive Procurement Brief",
      desc: "Full cost-benefit analysis: hidden project costs, scenarios & executive financial dashboard.",
      view: "/big-bad-mats-procurement-brief",
      pdf: "/big-bad-mats-procurement-brief.pdf",
    },
  ];

  const BBM_ROLES = {
    "/big-bad-mats-roi": ["Asset Manager", "Procurement"],
    "/big-bad-mats-tco": ["Procurement"],
    "/big-bad-mats-value-matrix": ["Asset Manager", "HSE / Safety", "Superintendent", "Engineer", "Procurement"],
    "/big-bad-mats-asset-roi": ["Asset Manager"],
    "/big-bad-mats-safety": ["HSE / Safety"],
    "/big-bad-mats-schedule": ["Superintendent"],
    "/big-bad-mats-engineering": ["Engineer"],
    "/big-bad-mats-sourcing": ["Procurement"],
    "/big-bad-mats-datasheet": ["Engineer", "Superintendent"],
    "/big-bad-mats-calculator": ["Asset Manager", "Procurement"],
    "/big-bad-mats-battlecard": ["Procurement", "Superintendent"],
    "/big-bad-mats-case-study": ["Asset Manager", "Superintendent", "Procurement"],
    "/big-bad-mats-sustainability": ["HSE / Safety", "Compliance"],
    "/big-bad-mats-warranty": ["Engineer", "Procurement", "Compliance"],
    "/big-bad-mats-industry/oil-gas": ["Asset Manager", "Engineer"],
    "/big-bad-mats-industry/pipeline-row": ["Engineer", "Superintendent"],
    "/big-bad-mats-industry/utility-td": ["HSE / Safety", "Superintendent"],
    "/big-bad-mats-industry/fire-hazmat": ["HSE / Safety", "Compliance"],
    "/big-bad-mats-procurement-brief": ["Procurement"],
  };
  const BBM_SECTORS = {
    "/big-bad-mats-industry/oil-gas": ["Oil & Gas"],
    "/big-bad-mats-industry/pipeline-row": ["Pipeline"],
    "/big-bad-mats-industry/utility-td": ["Utility / T&D"],
    "/big-bad-mats-industry/fire-hazmat": ["Fire / Hazmat"],
  };
  const BBM_DOCS = BBM_RAW.map((d) => ({
    ...d,
    product: "Big Bad Mats",
    sectors: BBM_SECTORS[d.view] || MAT_SECTORS,
    roles: BBM_ROLES[d.view] || ["Procurement"],
  }));

  const FIRE_DOCS = FIRE_HAZMAT_DOCS.map((d) => ({
    ...d,
    pdf: `${process.env.REACT_APP_BACKEND_URL}/api/admin/collateral/${d.file}?token=${localStorage.getItem("oh_token") || ""}`,
  }));

  const DOCUMENTS = [...BBM_DOCS, ...CHEM_DOCS, ...FIRE_DOCS, ...FIRE_SERVICE_DOCS];

  const PRODUCT_OPTS = ["All Products", ...Array.from(new Set(DOCUMENTS.map((d) => d.product)))];
  const SECTOR_OPTS = ["All Sectors", ...Array.from(new Set(DOCUMENTS.flatMap((d) => d.sectors))).sort()];
  const ROLE_OPTS = ["All Roles", ...Array.from(new Set(DOCUMENTS.flatMap((d) => d.roles))).sort()];

  const filteredDocs = DOCUMENTS.filter((d) => {
    if (docProduct !== "All Products" && d.product !== docProduct) return false;
    if (docSector !== "All Sectors" && !d.sectors.includes(docSector)) return false;
    if (docRole !== "All Roles" && !d.roles.includes(docRole)) return false;
    if (docSearch && !(`${d.title} ${d.desc}`.toLowerCase().includes(docSearch.toLowerCase()))) return false;
    return true;
  });

  const tpOf = (s) => counts[(s.data.email || "").toLowerCase()]?.total || 0;
  const visibleSubs = subs
    .filter((s) => {
      if (newOnly && s.status !== "new") return false;
      if (subSource !== "All Sources" && leadSource(s) !== subSource) return false;
      if (subProduct !== "All Products" && leadProduct(s) !== subProduct) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === "engaged") {
        const diff = tpOf(b) - tpOf(a);
        if (diff !== 0) return diff;
      }
      return (b.created_at || "").localeCompare(a.created_at || "");
    });
  const SUB_PRODUCT_OPTS = ["All Products", "Big Bad Mats", "BoneDry", "Pinkwater", "—"];

  const sourceCounts = subs.reduce((a, s) => { const k = leadSource(s); a[k] = (a[k] || 0) + 1; return a; }, {});

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-charcoal text-white">
      {/* Top bar */}
      <header className="border-b border-white/10 bg-ink sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={IMAGES.markWhite} alt="OneHarvest" className="h-9 w-9 object-contain" />
            <div>
              <div className="font-display font-extrabold text-sm tracking-tight">ONEHARVEST</div>
              <div className="text-[10px] font-mono text-flame tracking-widest">ADMIN CONSOLE</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button data-testid="admin-send-digest" onClick={sendDigest} disabled={sendingDigest} className="inline-flex items-center gap-2 border border-white/15 px-3 py-2 text-sm hover:border-flame hover:text-flame transition-colors disabled:opacity-50">
              {sendingDigest ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              <span className="hidden sm:inline">Email digest</span>
            </button>
            <button data-testid="admin-refresh" onClick={load} className="p-2 hover:text-flame transition-colors"><RefreshCw className="h-4 w-4" /></button>
            <span className="text-sm text-white/50 hidden sm:block">{user?.email}</span>
            <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-sm hover:border-flame hover:text-flame transition-colors">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex items-center gap-1 mb-8 border-b border-white/10" data-testid="admin-tabs">
          {[{ k: "leads", label: "Leads & Collateral", icon: Inbox }, { k: "activity", label: "Activity", icon: Activity }, { k: "analytics", label: "Analytics", icon: TrendingUp }, { k: "outreach", label: "Email Outreach", icon: Send }, { k: "mats", label: "Mats", icon: Boxes }, { k: "visitors", label: "Visitors", icon: Eye }, { k: "partners", label: "Partners", icon: Users }].map((t) => (
            <button key={t.k} data-testid={`tab-${t.k}`} onClick={() => setTab(t.k)}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === t.k ? "border-flame text-flame" : "border-transparent text-white/50 hover:text-white"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
              {t.k === "leads" && stats?.new > 0 && (
                <span data-testid="leads-new-badge" className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-flame text-white text-[11px] font-bold leading-none">{stats.new}</span>
              )}
            </button>
          ))}
        </div>

        {tab === "outreach" && <OutreachCampaigns />}

        {tab === "activity" && <ActivityFeed />}

        {tab === "analytics" && <RangeAnalytics />}

        {tab === "visitors" && <VisitorAnalytics />}

        {tab === "partners" && <PartnersAdmin />}

        {tab === "mats" && <MatManager />}

        {tab === "leads" && (<div className="flex flex-col">
        {/* Stats */}
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          {STAT_CARDS.map((c) => (
            <div key={c.label} data-testid={`stat-${c.label.toLowerCase().replace(/\W+/g, "-")}`} className="bg-ink border border-white/10 p-6">
              <div className="flex items-center justify-between">
                <c.icon className="h-5 w-5 text-flame" />
                <span className="font-display font-black text-3xl">{c.value}</span>
              </div>
              <div className="mt-3 text-xs text-white/50 uppercase tracking-widest">{c.label}</div>
            </div>
          ))}
        </div>

        {/* Top engaged leads */}
        {topEngaged.length > 0 && (
          <div className="mt-6" data-testid="top-engaged">
            <div className="flex items-center gap-2 mb-3">
              <Activity className="h-4 w-4 text-emerald-400" />
              <h2 className="font-display font-bold text-sm uppercase tracking-widest text-white/70">Top Engaged Leads</h2>
              <span className="text-xs text-white/40">— your most-touched contacts</span>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {topEngaged.map((x) => {
                const src = leadSource(x.sub);
                return (
                  <button key={x.email} data-testid={`top-engaged-${x.email}`} onClick={() => openSubmission(x.sub)}
                    className="group text-left bg-ink border border-emerald-400/30 hover:border-emerald-400/60 p-5 transition-colors">
                    <div className="flex items-center justify-between gap-2">
                      {x.total >= HOT_LEAD_THRESHOLD ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 border border-flame/50 text-flame bg-flame/15">
                          <Flame className="h-3 w-3" /> Hot lead · {x.total}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 border border-emerald-400/40 text-emerald-400 bg-emerald-400/10">
                          <Activity className="h-3 w-3" /> {x.total} touchpoints
                        </span>
                      )}
                      <ExternalLink className="h-4 w-4 text-white/20 group-hover:text-emerald-400 transition-colors" />
                    </div>
                    <div className="mt-3 font-display font-bold text-lg truncate">{x.sub.data.name}</div>
                    <div className="text-sm text-white/50 truncate">{x.sub.data.company || x.sub.data.email}</div>
                    <div className="mt-2 flex items-center gap-2">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${SOURCE_STYLE[src]}`}>{src}</span>
                      <span className="text-[11px] text-white/40">latest {timeAgo(x.sub.created_at)}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Documents / Sales Collateral */}
        <div className="mt-8 order-last" data-testid="admin-documents">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <button data-testid="collateral-toggle" onClick={() => setCollateralOpen((v) => !v)} className="flex items-center gap-2 group">
              <ChevronDown className={`h-4 w-4 text-white/40 transition-transform ${collateralOpen ? "" : "-rotate-90"}`} />
              <FileBarChart2 className="h-4 w-4 text-flame" />
              <h2 className="font-display font-bold text-sm uppercase tracking-widest text-white/70 group-hover:text-white transition-colors">Sales Collateral</h2>
              <span className="text-xs text-white/40" data-testid="doc-count">({filteredDocs.length} of {DOCUMENTS.length})</span>
            </button>
            {collateralOpen && (<div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="h-3.5 w-3.5 text-white/40 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input value={docSearch} onChange={(e) => setDocSearch(e.target.value)} placeholder="Search collateral…" data-testid="doc-search"
                  className="bg-ink border border-white/15 pl-8 pr-3 py-2 text-sm text-white w-48 focus:border-flame focus:outline-none" />
              </div>
              {[
                { val: docProduct, set: setDocProduct, opts: PRODUCT_OPTS, tid: "filter-product" },
                { val: docSector, set: setDocSector, opts: SECTOR_OPTS, tid: "filter-sector" },
                { val: docRole, set: setDocRole, opts: ROLE_OPTS, tid: "filter-role" },
              ].map((f) => (
                <select key={f.tid} value={f.val} onChange={(e) => f.set(e.target.value)} data-testid={f.tid}
                  className="bg-ink border border-white/15 px-3 py-2 text-sm text-white focus:border-flame focus:outline-none">
                  {f.opts.map((o) => <option key={o} value={o} className="bg-ink">{o}</option>)}
                </select>
              ))}
              {(docProduct !== "All Products" || docSector !== "All Sectors" || docRole !== "All Roles" || docSearch) && (
                <button data-testid="filter-clear" onClick={() => { setDocProduct("All Products"); setDocSector("All Sectors"); setDocRole("All Roles"); setDocSearch(""); }}
                  className="px-3 py-2 text-sm border border-white/15 text-white/60 hover:text-flame hover:border-flame transition-colors">Clear</button>
              )}
            </div>)}
          </div>
          {collateralOpen && (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {filteredDocs.map((d) => (
              <div key={d.title} data-testid={`document-${d.view}`} className="bg-ink border border-white/10 p-5 flex flex-col">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 flex items-center justify-center bg-flame/10 border border-flame/30 flex-shrink-0">
                    <d.icon className="h-5 w-5 text-flame" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-bold leading-tight">{d.title}</div>
                    <p className="mt-1 text-sm text-white/50 leading-snug">{d.desc}</p>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <span className="text-[9px] uppercase tracking-wider bg-white/5 border border-white/10 text-white/60 px-1.5 py-0.5">{d.product}</span>
                  {d.roles.map((r) => (
                    <span key={r} className="text-[9px] uppercase tracking-wider bg-flame/10 border border-flame/20 text-flame/90 px-1.5 py-0.5">{r}</span>
                  ))}
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <a href={d.view} target="_blank" rel="noreferrer" data-testid={`doc-view-${d.view}`}
                    className="inline-flex items-center gap-2 border border-white/15 px-3 py-2 text-xs hover:border-flame hover:text-flame transition-colors">
                    <ExternalLink className="h-3.5 w-3.5" /> {d.tool ? "Open Tool" : "View"}
                  </a>
                  {d.pdf && (
                    <a href={d.pdf} download data-testid={`doc-download-${d.pdf}`}
                      className="inline-flex items-center gap-2 bg-flame text-white px-3 py-2 text-xs font-semibold hover:bg-flame/90 transition-colors">
                      <Download className="h-3.5 w-3.5" /> Download PDF
                    </a>
                  )}
                </div>
              </div>
            ))}
            {filteredDocs.length === 0 && (
              <div className="col-span-full text-center text-white/40 py-10 border border-dashed border-white/10" data-testid="no-docs">No collateral matches these filters.</div>
            )}
          </div>
          )}
        </div>

        {/* Filters */}
        <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="submission-filters">
          {["all", "quote", "contact"].map((f) => (
            <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
              className={`px-4 py-2 text-sm border transition-colors ${filter === f ? "bg-flame text-white border-flame" : "border-white/15 text-white/60 hover:text-white"}`}>
              {f === "all" ? "All" : f === "quote" ? "Quote Requests" : "Contact Messages"}
            </button>
          ))}
          <span className="mx-1 h-6 w-px bg-white/10" />
          {["All Sources", "Calculator", "Download", "Quote Form", "Contact", "Outreach"].map((src) => (
            <button key={src} data-testid={`source-${src.toLowerCase().replace(/\W+/g, "-")}`} onClick={() => setSubSource(src)}
              className={`px-3 py-2 text-sm border transition-colors ${subSource === src ? "bg-flame text-white border-flame" : "border-white/15 text-white/60 hover:text-white"}`}>
              {src}{src !== "All Sources" && sourceCounts[src] ? ` (${sourceCounts[src]})` : ""}
            </button>
          ))}
          <select value={subProduct} onChange={(e) => setSubProduct(e.target.value)} data-testid="source-product-filter"
            className="bg-ink border border-white/15 px-3 py-2 text-sm text-white focus:border-flame focus:outline-none">
            {SUB_PRODUCT_OPTS.map((o) => <option key={o} value={o} className="bg-ink">{o}</option>)}
          </select>
          <div className="ml-auto flex items-center gap-1 border border-white/15" data-testid="sort-toggle">
            <span className="pl-3 pr-1 text-[11px] uppercase tracking-widest text-white/40">Sort</span>
            {[{ k: "recent", label: "Newest" }, { k: "engaged", label: "Most engaged", icon: Activity }].map((o) => (
              <button key={o.k} data-testid={`sort-${o.k}`} onClick={() => setSortBy(o.k)}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm transition-colors ${sortBy === o.k ? "bg-flame text-white" : "text-white/60 hover:text-white"}`}>
                {o.icon && <o.icon className="h-3.5 w-3.5" />} {o.label}
              </button>
            ))}
          </div>
        </div>

        {/* Leads list header + export */}
        <div className="mt-6 flex items-center justify-between gap-3" data-testid="leads-list-header">
          <div className="flex items-center gap-3">
            <button data-testid="filter-new-only" onClick={() => setNewOnly((v) => !v)}
              className={`inline-flex items-center gap-2 px-3 py-2 text-sm border transition-colors ${newOnly ? "bg-flame text-white border-flame" : "border-white/15 text-white/60 hover:text-white hover:border-white/30"}`}>
              <Circle className={`h-3.5 w-3.5 ${newOnly ? "fill-white" : ""}`} /> New only
            </button>
            <div className="text-xs uppercase tracking-widest text-white/40">Showing {visibleSubs.length} lead{visibleSubs.length === 1 ? "" : "s"}</div>
          </div>
          <button data-testid="export-csv" onClick={exportCsv} disabled={visibleSubs.length === 0}
            className="inline-flex items-center gap-2 border border-white/15 px-3 py-2 text-sm hover:border-flame hover:text-flame transition-colors disabled:opacity-40 disabled:hover:border-white/15 disabled:hover:text-white">
            <Download className="h-4 w-4" /> Download CSV
          </button>
        </div>

        {/* Submissions list (full width) */}
        <div className="mt-3 bg-ink border border-white/10" data-testid="submission-list">
          {loading ? (
            <div className="p-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-flame" /></div>
          ) : visibleSubs.length === 0 ? (
            <div className="p-16 text-center text-white/40">No submissions match these filters.</div>
          ) : (
            <div className="divide-y divide-white/10">
              {visibleSubs.map((s) => {
                const src = leadSource(s);
                const prod = leadProduct(s);
                const tp = counts[(s.data.email || "").toLowerCase()]?.total || 0;
                return (
                <button key={s.id} data-testid={`submission-row-${s.id}`} onClick={() => openSubmission(s)}
                  className={`w-full text-left p-5 hover:bg-white/5 transition-colors flex items-center justify-between gap-4 group ${selected?.id === s.id ? "bg-white/5" : ""}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${SOURCE_STYLE[src]}`} data-testid={`row-source-${s.id}`}>{src}</span>
                      {prod !== "—" && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-white/15 text-white/50">{prod}</span>}
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-white/10 text-white/40">{s.type === "quote" ? "Quote Request" : "Contact Message"}</span>
                      <span className="font-display font-semibold truncate">{s.data.name}</span>
                      {tp > 1 && (
                        tp >= HOT_LEAD_THRESHOLD ? (
                          <span data-testid={`row-touchpoints-${s.id}`} title={`${tp} interactions — warming lead`}
                            className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 border border-flame/50 text-flame bg-flame/15">
                            <Flame className="h-3 w-3" /> Hot · {tp}
                          </span>
                        ) : (
                          <span data-testid={`row-touchpoints-${s.id}`} title={`${tp} interactions from this contact`}
                            className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 border border-emerald-400/40 text-emerald-400 bg-emerald-400/10">
                            <Activity className="h-3 w-3" /> {tp} touchpoints
                          </span>
                        )
                      )}
                    </div>
                    <div className="mt-1 text-sm text-white/50 truncate">{s.data.company || s.data.email} · {timeAgo(s.created_at)}</div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${STATUS_STYLE[s.status]}`}>{s.status.replace("_", " ")}</span>
                    <ExternalLink className="h-4 w-4 text-white/20 group-hover:text-flame transition-colors" />
                  </div>
                </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Expanded lead detail modal */}
        {selected && (
          <div data-testid="submission-modal" onClick={closeModal}
            className="fixed inset-0 z-[60] flex items-start justify-center p-4 sm:p-8 overflow-y-auto bg-black/70 backdrop-blur-sm">
            <div className="relative bg-ink border border-white/10 w-full max-w-3xl my-4" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="sticky top-0 bg-ink border-b border-white/10 p-6 flex items-start justify-between gap-4 z-10">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${SOURCE_STYLE[leadSource(selected)]}`}>{leadSource(selected)}</span>
                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-white/15 text-white/50">{selected.type === "quote" ? "Quote Request" : "Contact Message"}</span>
                    {leadProduct(selected) !== "—" && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-white/15 text-white/50">{leadProduct(selected)}</span>}
                  </div>
                  <h3 className="mt-2 font-display font-black text-2xl truncate">{selected.data.name}</h3>
                  <div className="text-sm text-white/50">{new Date(selected.created_at).toLocaleString()} · {timeAgo(selected.created_at)}</div>
                </div>
                <button data-testid="modal-close" onClick={closeModal} className="p-2 text-white/40 hover:text-white transition-colors flex-shrink-0"><X className="h-5 w-5" /></button>
              </div>

              {detailLoading ? (
                <div className="p-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-flame" /></div>
              ) : (
              <div className="p-6 space-y-6">
                {/* Who + Status */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="bg-charcoal/40 border border-white/10 p-4">
                    <div className="kicker text-white/40 mb-3">Who</div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between gap-2">
                        <a href={`mailto:${selected.data.email}`} className="text-flame truncate">{selected.data.email}</a>
                        <button data-testid="copy-email" onClick={() => copy(selected.data.email)} className="text-white/40 hover:text-flame flex-shrink-0"><Copy className="h-3.5 w-3.5" /></button>
                      </div>
                      {selected.data.company && <div className="text-white/80">{selected.data.company}</div>}
                      {selected.data.phone && <a href={`tel:${selected.data.phone}`} className="text-white/80 block hover:text-flame">{selected.data.phone}</a>}
                    </div>
                  </div>
                  <div className="bg-charcoal/40 border border-white/10 p-4">
                    <div className="kicker text-white/40 mb-3">Set status</div>
                    <div className="flex flex-wrap gap-2">
                      {["new", "in_progress", "closed"].map((st) => (
                        <button key={st} data-testid={`set-status-${st}`} onClick={() => updateStatus(selected.id, st)}
                          className={`px-3 py-2 text-xs uppercase tracking-widest border transition-colors ${selected.status === st ? STATUS_STYLE[st] : "border-white/15 text-white/50 hover:text-white"}`}>
                          {st.replace("_", " ")}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* What's requested */}
                <div>
                  <div className="kicker text-white/40 mb-3">What's requested</div>
                  <dl className="text-sm border border-white/10 divide-y divide-white/5" data-testid="request-details">
                    {Object.entries(selected.data)
                      .filter(([k, v]) => !["name", "email", "company", "phone", "source", "message"].includes(k) && v && (!Array.isArray(v) || v.length))
                      .map(([k, v]) => (
                        <div key={k} className="grid grid-cols-3 gap-3 p-3">
                          <dt className="text-white/40 uppercase text-[11px] tracking-widest pt-0.5">{prettyLabel(k)}</dt>
                          <dd className="col-span-2 text-white/90 break-words">{Array.isArray(v) ? v.join(", ") : String(v)}</dd>
                        </div>
                      ))}
                    {selected.data.message && (
                      <div className="p-3">
                        <dt className="text-white/40 uppercase text-[11px] tracking-widest mb-2">Message</dt>
                        <dd className="text-white/90 whitespace-pre-wrap leading-relaxed">{selected.data.message}</dd>
                      </div>
                    )}
                    {Object.entries(selected.data).filter(([k, v]) => !["name", "email", "company", "phone", "source"].includes(k) && v && (!Array.isArray(v) || v.length)).length === 0 && (
                      <div className="p-3 text-white/40">No additional request details were captured.</div>
                    )}
                  </dl>
                </div>

                {/* Engagement timeline */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Activity className="h-4 w-4 text-flame" />
                    <div className="kicker text-white/40">Engagement activity{detail?.history_count > 1 ? <span className="text-white/30 normal-case tracking-normal"> · {detail.history_count} touchpoints from this contact</span> : null}</div>
                  </div>
                  {detail?.engagement?.length ? (
                    <ol className="relative border-l border-white/10 ml-2 space-y-4" data-testid="engagement-timeline">
                      {detail.engagement.map((e, i) => (
                        <li key={i} className="ml-4">
                          <span className={`absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full ${e.kind === "outreach_click" ? "bg-emerald-400" : e.kind === "submission" ? "bg-flame" : "bg-white/30"}`} />
                          <div className="flex items-start justify-between gap-3">
                            <div className="text-sm text-white/90">{e.title}</div>
                            <div className="text-[11px] text-white/40 whitespace-nowrap">{timeAgo(e.at)}</div>
                          </div>
                          {e.detail && e.detail !== "—" && <div className="text-xs text-white/40 mt-0.5">{e.detail}</div>}
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <div className="text-sm text-white/40 border border-dashed border-white/10 p-4" data-testid="engagement-empty">No prior engagement recorded for this contact.</div>
                  )}
                </div>
              </div>
              )}

              {/* Footer actions */}
              <div className="sticky bottom-0 bg-ink border-t border-white/10 p-4 flex items-center justify-between gap-3">
                <button data-testid="modal-delete" onClick={() => remove(selected.id)} className="inline-flex items-center gap-2 text-sm text-white/40 hover:text-destructive transition-colors"><Trash2 className="h-4 w-4" /> Delete</button>
                <a href={`mailto:${selected.data.email}?subject=Re: Your ${selected.type === "quote" ? "quote request" : "message"} — OneHarvest`} data-testid="modal-reply" className="inline-flex items-center gap-2 bg-flame text-white px-4 py-2 text-sm font-semibold hover:bg-flame/90 transition-colors"><Mail className="h-4 w-4" /> Reply by email</a>
              </div>
            </div>
          </div>
        )}
        </div>)}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-3">
      <dt className="text-white/40 uppercase text-[11px] tracking-widest pt-0.5">{label}</dt>
      <dd className="col-span-2 text-white/90 break-words">{value}</dd>
    </div>
  );
}

const FIELD_LABELS = {
  kit_version: "ERMK Version", fleet_size: "Fleet Size", industry: "Industry / Context",
  products: "Products of Interest", message: "Message", source: "Source",
};
function prettyLabel(k) {
  return FIELD_LABELS[k] || k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const secs = Math.floor((Date.now() - then) / 1000);
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}
